import { supabase } from "./supabase";

/* Creating a COACH account from the web.
 *
 * 🔴 HIS 2026-09-13 ASK, and it closes a hole he had already noticed once himself (*"why is it
 * bringing us straight to sign in? how one signs up if has no iphone at all?"*). The answer then
 * was a TestFlight link. It stopped being good enough the moment a real coach turned out not to
 * own an iPhone.
 *
 * ⚠️ **IT MAKES A COACH, AND THERE IS NO ROLE QUESTION, BY HIS OWN DECISION** (2026-08-29: *"the
 * client side just gives you a link for the app"*). The web console IS the coach product — every
 * screen in it is a coach's — so asking which kind of account this is would be offering a choice
 * with one usable answer. An athlete who signed up here would land in a console with no training
 * of their own in it.
 *
 * ⚠️ **THE PROFILE ROW IS THE ACCOUNT.** `auth.users` alone is not an account this app can use:
 * `account_role` lives on `profiles`, and without that row the coach is a signed-in stranger with
 * no role, which is exactly the state Google sign-in has been leaving people in on this page.
 * `ensureCoachProfile` is therefore called from TWO places — straight after sign-up when a session
 * comes back, and again after any sign-in that finds no row.
 */

export type SignUpOutcome =
  /** Session in hand: the app can render immediately. */
  | { kind: "signedIn" }
  /** The project has email confirmation on, so there is no session yet. */
  | { kind: "confirmEmail" }
  | { kind: "failed"; message: string };

export async function signUpCoach(args: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}): Promise<SignUpOutcome> {
  const { data, error } = await supabase.auth.signUp({
    email: args.email.trim(),
    password: args.password,
    // Carried on the auth user so the profile can still be written later, on first sign-in,
    // when email confirmation means there is no session right now to write it with.
    // ⚠️ `signup_surface` IS THE BELT; the placeholder SHAPE below is the braces. A Google
    // sign-up cannot carry metadata at all, so the upgrade may not depend on this alone.
    options: {
      data: {
        first_name: args.firstName.trim(),
        last_name: args.lastName.trim(),
        signup_surface: "web-coach",
      },
    },
  });

  if (error) return { kind: "failed", message: error.message };

  // ⚠️ A USER WITHOUT A SESSION IS NOT A FAILURE — it is Supabase's "confirm your email" path, the
  // same branch the iOS sign-up already guards (`guard response.session != nil`). Treating it as
  // success would leave somebody staring at a login form wondering why their new password does not
  // work; treating it as an error would be a lie about an account that now exists.
  if (data.session === null) return { kind: "confirmEmail" };

  const wrote = await ensureCoachProfile();
  return wrote === null ? { kind: "signedIn" } : { kind: "failed", message: wrote };
}

/** The two names a sign-up carries, however it arrived. */
export function namesFromMetadata(meta: {
  first_name?: string;
  last_name?: string;
  name?: string;
}): { first: string; last: string } {
  // Google hands back one `name`; the form hands back two fields. Either way the row gets a name,
  // because a roster row with no name reads as a broken account rather than an empty one.
  const fromGoogle = (meta.name ?? "").trim().split(" ");
  const first = (meta.first_name ?? fromGoogle[0] ?? "").trim();
  const last = (meta.last_name ?? fromGoogle.slice(1).join(" ")).trim();
  return { first, last };
}

/** What the database holds for this account, as far as this decision cares. */
export type ProfileShape = {
  account_role: string | null;
  first_name: string | null;
};

/** What `ensureCoachProfile` should do with the row it found (or did not). */
export type ProfileAction = "insert" | "upgrade" | "leave";

/* 🔴 **THE ONE DECISION, PURE, SO IT CAN BE TESTED WITHOUT A NETWORK — AND IT IS THE WHOLE OF R15.**
 *
 * MEASURED LIVE 2026-09-18, and the defect had never executed because nobody has ever signed up on
 * the web: `auth.users` carries a trigger `on_auth_user_created` → `handle_new_user`, which is
 * `INSERT INTO public.profiles (id) VALUES (NEW.id)` and writes NOTHING else. `account_role`
 * defaults to `'client'`; `first_name` is nullable with no default. So the instant an auth user
 * exists there IS a `profiles` row — and the old `ensureCoachProfile` asked only *"is there a
 * row?"*, found one, and returned. A coach who signed up on the web became a NAMELESS CLIENT:
 * `coach_profiles_owner_all` requires `account_role = 'coach'`, so their coach page was refused,
 * their roster row had no name, and nothing said why.
 *
 * ⚠️ **"A ROW EXISTS" IS NOT "THE ACCOUNT WAS SET UP"** — the web read the trigger's PLACEHOLDER as
 * the phone's finished profile. That is the codex's own shape, and it is why the discriminator here
 * is the row's SHAPE rather than its existence.
 *
 * 🔴 **HIS RULE, 2026-09-18: *"the web app is only for coaches so the role needs to be automatically
 * as coach, no choice for the user."*** Which must not become *any visitor becomes a coach*, so:
 *
 *   · `insert`  — no row at all. Nothing created it, so this account is being set up here.
 *   · `upgrade` — the trigger's placeholder EXACTLY: the role still at its `'client'` default AND no
 *                 first name. MEASURED that only the trigger produces this, because the phone's
 *                 `signUpAndSaveProfile` upserts the whole profile — name AND a chosen role — in the
 *                 same breath as its auth sign-up. The metadata stamp is checked too where it
 *                 exists, but may not be REQUIRED: a Google sign-up carries no `options.data` at
 *                 all, and Google is a door on this very page.
 *   · `leave`   — anything else. A phone-made row is never touched, whatever its role, because it
 *                 cannot satisfy both halves.
 *
 * ⚠️ **THE ONE WINDOW, STATED RATHER THAN HIDDEN:** a phone account whose profile upsert FAILED
 * would sit in the placeholder shape, and signing in on the web would make it a coach. It is narrow
 * (the upsert follows the sign-up immediately, on the same device) and it costs only coach SCREENS,
 * never anybody else's data — every coach policy also requires an accepted `coach_clients` row.
 * Saying so here is cheaper than discovering it from a support message.
 */
export function profileAction(
  existing: ProfileShape | null,
  meta: { signup_surface?: string },
): ProfileAction {
  if (existing === null) return "insert";
  const isPlaceholder =
    (existing.account_role ?? "client") === "client" &&
    (existing.first_name ?? "").trim() === "";
  if (isPlaceholder) return "upgrade";
  // The stamp alone never promotes a finished row — it is corroboration, not authority.
  void meta.signup_surface;
  return "leave";
}

/* Writes or completes the `profiles` row for whoever is signed in. Returns null on success or the
 * server's own sentence.
 *
 * ⚠️ **IT NEVER OVERWRITES A FINISHED ROW.** A coach or an athlete who signed up on the phone keeps
 * every field, including a role this function would otherwise stamp over — see `profileAction`.
 */
export async function ensureCoachProfile(): Promise<string | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData.session?.user;
  if (user === undefined) return null;

  const { data: existing, error: readError } = await supabase
    .from("profiles")
    .select("id, account_role, first_name")
    .eq("id", user.id)
    .maybeSingle();
  if (readError) return readError.message;

  const meta = (user.user_metadata ?? {}) as {
    first_name?: string;
    last_name?: string;
    name?: string;
    signup_surface?: string;
  };
  const action = profileAction(
    existing === null ? null : { account_role: existing.account_role, first_name: existing.first_name },
    meta,
  );
  if (action === "leave") return null;

  const { first, last } = namesFromMetadata(meta);

  if (action === "upgrade") {
    // The owner's own `auth.uid() = id` UPDATE policy already admits this — NO policy change.
    const { error } = await supabase
      .from("profiles")
      .update({ first_name: first, last_name: last, account_role: "coach" })
      .eq("id", user.id);
    return error ? error.message : null;
  }

  const { error: writeError } = await supabase.from("profiles").insert({
    id: user.id,
    first_name: first,
    last_name: last,
    account_role: "coach",
  });
  return writeError ? writeError.message : null;
}

/* 🔴 **WHO MAY USE THE WEB CONSOLE — HIS RULE'S SECOND HALF, 2026-09-18.**
 *
 * *"the web app is only for coaches so the role needs to be automatically as coach, no choice for
 * the user."* Making every account CREATED here a coach is the first half; this is the half that
 * stops that becoming *any visitor becomes a coach*. An athlete who made their account on the
 * PHONE and signs in here is NEVER promoted — `profileAction` leaves their row alone — so without
 * this they would be handed the coach console: a roster of nobody, a library that is not theirs,
 * and no sign anywhere that they are in the wrong place.
 *
 * ⚠️ **`unknown` IS ITS OWN ANSWER AND MUST NOT FALL THROUGH TO EITHER SIDE.** A failed read is
 * not "not a coach" (that would lock a real coach out of their own console on a flaky connection)
 * and not "a coach" (that would show the console to anybody the moment the query errors). The
 * caller keeps waiting and says so.
 */
export type WebAccess = "coach" | "notACoach" | "unknown";

export function webAccess(role: string | null | undefined): WebAccess {
  if (role === undefined || role === null) return "unknown";
  return role === "coach" ? "coach" : "notACoach";
}

/** Completes the profile if it needs completing, then says whether this account may be here. */
export async function ensureCoachAndCheckAccess(): Promise<WebAccess> {
  const wrote = await ensureCoachProfile();
  // A write that failed leaves the row in whatever state it was in; the read below is still the
  // honest question, so the error is not swallowed silently but it does not decide access either.
  if (wrote !== null) console.warn("[signUp] profile write failed:", wrote);

  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData.session?.user;
  if (user === undefined) return "unknown";

  const { data, error } = await supabase
    .from("profiles").select("account_role").eq("id", user.id).maybeSingle();
  if (error || data === null) return "unknown";
  return webAccess(data.account_role);
}
